import axios from 'axios'
import { cleanEmbeddedImagesWithinReply } from '@/helpers/forum'
import { fuzzySearch } from '@/helpers/fuzzy-search'
import moment from 'moment-timezone'
import Vue from 'vue'

const COMPONENT_POST = 'forum_post'
const SORT_RULES = {
  'Post Created': 0,
  'Last Replied': 1
}

export default {
  namespaced: true,
  state: {
    pageSize: 10,
    newPost: {
      title: '',
      categoryId: null,
      authorId: null,
      content: {}
    },
    postTagsId: [],
    newReply: {
      parentPostId: null,
      parentReplyId: null,
      authorId: null,
      content: {}
    },
    // TODO: combine the objects later
    postsMap: {},
    postsTagsMap: {},
    postsReplyNumberMap: {},
    postReplies: [],
    threadsMap: {},
    childReplies: {},
    allReplies: [], // all replies data
    postAllRepliesContent: {}, // prepare for search
    searchText: undefined,
    filteredThreads: [],
    likesForPage: [],
    searchResults: {},
    points: {},
    sortRuleIndex: 0,
    sortRules: SORT_RULES,
    forumTags: []
  },

  getters: {
    postsArr: state => {
      return Object.values(state.postsMap)
    },

    threadsArr: state => {
      return Object.values(state.threadsMap)
    }
  },

  mutations: {
    // mutations for new post
    updateNewPost (state, post) {
      state.newPost = post
    },

    updatePostTagsId (state, tagsId) {
      state.postTagsId = tagsId
    },

    initPosts (state, results) {
      const { posts, postsTags, postsReplyNumber } = results
      state.postsMap = posts.reduce((acc, cur) => {
        acc[cur.id] = cur
        return acc
      }, {})
      state.postsTagsMap = postsTags
      state.postsReplyNumberMap = postsReplyNumber
    },

    initThreads (state, threads) {
      state.threadsMap = threads.reduce((acc, cur) => {
        acc[cur.id] = cur
        return acc
      }, {})
    },

    clearPostOnSubmission (state) {
      state.newPost.title = ''
      state.newPost.createdAt = null
      state.newPost.categoryId = null
      state.newPost.tagsId = []
      state.newPost.authorId = null
      state.newPost.content = {}
    },

    // mutations for new reply
    updateNewReply (state, reply) {
      state.newReply = reply
    },

    clearReplyOnSubmission (state) {
      state.newReply.parentPostId = null
      state.newReply.parentReplyId = null
      state.newReply.content = {}
      state.newReply.authorId = null
    },

    initPostReplies (state, postReplies) {
      state.postReplies = postReplies
    },

    updatePostReplies (state, reply) {
      state.postReplies.push(reply)
    },

    initChildReplies (state, { replies, parentReplyId }) {
      state.childReplies[parentReplyId] = replies
    },

    updateChildReplies (state, { reply, parentReplyId }) {
      state.childReplies[parentReplyId].push(reply)
    },

    updateAllReplies (state, data) {
      state.allReplies = data
    },

    refreshPostAllRepliesContent (state) {
      state.postAllRepliesContent = {}
      state.allReplies.forEach(reply => {
        if (!state.postAllRepliesContent.hasOwnProperty(reply.parentPostId)) {
          state.postAllRepliesContent[reply.parentPostId] = {
            id: reply.parentPostId,
            content: ''
          }
        }
        const doc = JSON.parse(reply.content)
        doc.content.forEach(block => {
          if (!block.hasOwnProperty('content')) {
            return
          }
          block.content.filter(item => item.type === 'text').forEach(line => {
            state.postAllRepliesContent[reply.parentPostId].content += `${line.text} `
          })
        })
      })
    },

    updateSearchText (state, { searchText }) {
      state.searchText = searchText
    },

    updateFilteredThreads (state, filteredThreads) {
      state.filteredThreads = filteredThreads
    },

    updateLikesForPage (state, likesForPage) {
      state.likesForPage = likesForPage
    },

    updateSearchResults (state, searchResults) {
      state.searchResults = searchResults
    },

    updatePoints (state, points) {
      state.points = points
    },

    updateBestAnswer (state, { postId, replyId, resolved }) {
      Vue.set(state.postsMap[postId], 'resolved', resolved)
      Vue.set(state.threadsMap[postId], 'resolved', resolved)
      const targetReply = state.postReplies.find(reply => reply.id === replyId)
      if (targetReply) {
        Vue.set(targetReply, 'isBestAnswer', resolved)
      }
    },

    updateSortRuleIndex (state, sortRuleIndex) {
      state.sortRuleIndex = sortRuleIndex
    },

    updateForumTags (state, forumTags) {
      state.forumTags = forumTags
    },

    updatePostsMap (state, postData) {
      Vue.set(state.postsMap, postData.id, postData)
    },

    updateThreadsMap (state, threadData) {
      Vue.set(state.threadsMap, threadData.id, threadData)
    },

    updatePostsTagsMap (state, { postId, postTagsData }) {
      Vue.set(state.postsTagsMap, postId, postTagsData)
    }
  },

  actions: {
    /**
     * Fetches forum data for the forum homepage
     */
    async fetchPosts ({ commit }) {
      try {
        const results = (await axios.get(process.env.VUE_APP_API_URI + '/forum')).data
        commit('initPosts', results)
      } catch (error) {
        console.error(`Failed to fetch all forum data for forum homepage: ${error}`)
      }
    },

    async fetchPostsFirstPage ({ commit }) {
      try {
        const results = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/initial`)).data
        commit('initPosts', results)
      } catch (error) {
        console.error(`Failed to fetch all forum data for forum homepage: ${error}`)
      }
    },

    async fetchPostsForUser ({ commit }, { userId }) {
      try {
        const results = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/user/${userId}/posts`)).data
        commit('initPosts', results)
      } catch (error) {
        console.error(`Failed to fetch posts for user: ${error}`)
      }
    },

    /**
     * Creates the list of all existing posts.
     * TODO: research if process the data on server could be faster
     */
    getThreads ({ commit, state, getters }, { users }) {
      const threads = getters.postsArr.map(post => {
        const { id, title, authorId, createdAt, updatedAt, lastReplyAt, replierId, content, sticky, resolved } = post
        const author = users.find(user => user.id === authorId) || {}
        const lastReplier = users.find(user => user.id === replierId)
        const numReplies = state.postsReplyNumberMap[id] ? state.postsReplyNumberMap[id] : 0
        const lastUpdated = updatedAt
        const tags = state.postsTagsMap[id] ? state.postsTagsMap[id].map(item => item.name) : []
        return {
          id,
          title,
          content,
          createdAt,
          lastUpdated,
          lastReplyAt,
          author,
          lastReplier,
          numReplies,
          tags,
          sticky: Boolean(sticky),
          resolved: Boolean(resolved)
        }
      })

      commit('initThreads', threads)
    },

    /**
     * Fetches replies for post
     */
    async fetchPostReplies ({ commit }, { postId }) {
      try {
        const postReplies = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/post/${postId}/replies`)).data

        // Fetch the encoded images of the replies (if there's any).
        postReplies.forEach(postReply => {
          const postReplyContentJSON = JSON.parse(postReply.content)

          postReply.content = JSON.stringify(cleanEmbeddedImagesWithinReply(postReplyContentJSON))
        })

        commit('initPostReplies', postReplies)
      } catch (error) {
        console.error(`Failed to fetch replies for the post: ${error}`)
      }
    },

    /**
     * Update replies for post (when new reply is added)
     */
    async updatePostReplies ({ commit }, { addedReplyId }) {
      try {
        const reply = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/reply/${addedReplyId}`)).data[0]
        const replyContentJSON = JSON.parse(reply.content)

        reply.content = JSON.stringify(cleanEmbeddedImagesWithinReply(replyContentJSON))

        commit('updatePostReplies', reply)
      } catch (error) {
        console.error(`Failed to fetch single reply data: ${error}`)
      }
    },

    /**
     * Fetches replies for a reply
     */
    async fetchChildReplies ({ commit }, { parentReplyId }) {
      try {
        const replies = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/reply/replies/${parentReplyId}`)).data

        // Fetch the encoded images of the replies (if there's any).
        replies.forEach(reply => {
          const replyContentJSON = JSON.parse(reply.content)

          reply.content = JSON.stringify(cleanEmbeddedImagesWithinReply(replyContentJSON))
        })

        commit('initChildReplies', {
          replies,
          parentReplyId
        })
      } catch (error) {
        console.error(`Failed to fetch replies for a reply: ${error}`)
      }
    },

    /**
     * Update replies for a reply (when new reply is added)
     */
    async updateChildReplies ({ commit }, { addedReplyId, parentReplyId }) {
      try {
        const reply = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/reply/${addedReplyId}`)).data[0]
        const replyContentJSON = JSON.parse(reply.content)

        reply.content = JSON.stringify(cleanEmbeddedImagesWithinReply(replyContentJSON))

        commit('updateChildReplies', {
          reply,
          parentReplyId
        })
      } catch (error) {
        console.error(`Failed to fetch single reply data: ${error}`)
      }
    },

    /**
     * Add reply to database
     */
    async addReply ({ commit, state }, { reply, context }) {
      try {
        commit('updateNewReply', reply)
        const replyId = (await axios.post(process.env.VUE_APP_API_URI + '/forum/reply', {
          replyData: state.newReply,
          context: context
        })).data
        commit('clearReplyOnSubmission')
        return replyId
      } catch (error) {
        console.error(`Failed to add new reply to database in the store: ${error}`)
      }
    },

    /**
     * Add new Post to database
     */
    async addPost ({ commit, state }, { post, tagsId }) {
      try {
        commit('updateNewPost', post)
        commit('updatePostTagsId', tagsId)
        const newPostPromise = await axios.post(process.env.VUE_APP_API_URI + '/forum/post', {
          newPost: state.newPost,
          tagsId: state.postTagsId
        })
        commit('clearPostOnSubmission')
        // Update the postsMap
        const newPostId = newPostPromise.data
        const newPostData = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/post/${newPostId}`)).data
        commit('updatePostsMap', newPostData)
        commit('updateThreadsMap', newPostData)
        return newPostPromise
      } catch (error) {
        console.error(`Failed to add new post to database in the store: ${error}`)
      }
    },

    /**
     * Fetches and updates all the replies data in the forum
     * Prepare for search
     */
    async fetchAllReplies ({ commit }) {
      try {
        const replies = await axios.get(process.env.VUE_APP_API_URI + '/forum/replies')
        commit('updateAllReplies', replies.data)
        commit('refreshPostAllRepliesContent')
      } catch (error) {
        console.error(`Failed to fetch all replies for all posts: ${error}`)
      }
    },

    /**
     * Filter threads given filter text, tags in query params
     */
    async filterThreads ({ commit, state, dispatch, getters }, queryTags) {
      let filteredThreads = []
      switch (state.sortRuleIndex) {
        case 0:
          filteredThreads = getters.threadsArr.sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix())
          break
        case 1:
          filteredThreads = getters.threadsArr.sort((a, b) => moment(b.lastReplyAt || b.lastUpdated).unix() - moment(a.lastReplyAt || a.lastUpdated).unix())
          break
        default:
          filteredThreads = getters.threadsArr.sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix())
          break
      }

      if (state.searchText && state.searchText.length) {
        // Fuzzy search of posts based on posts (reply) title & content
        const options = {
          includeScore: true,
          includeMatches: true,
          minMatchCharLength: state.searchText.length / 2,
          threshold: 0.6,
          distance: 300
        }
        const keys = [{
          name: 'content',
          weight: 0.3
        }, {
          name: 'title',
          weight: 0.7
        }]
        await dispatch('fetchAllReplies')
        // Conduct fuzzy search and filter out results that has a score larger than 0.7
        const filterResults = fuzzySearch([...getters.threadsArr, ...Object.values(state.postAllRepliesContent)], state.searchText, keys, { ...options })
          .filter(result => result.score <= 0.7)
        const filteredIds = [...new Set(filterResults.map(result => result.item.id))]
        filteredThreads = filteredIds.reduce((acc, cur) => [...acc, getters.threadsArr.find(thread => thread.id === cur)], [])

        // Update matches information, prepare for search highlighting
        const searchResults = filterResults.reduce((acc, cur) => {
          if (!acc.hasOwnProperty(cur.item.id)) {
            acc[cur.item.id] = {
              score: cur.score,
              matches: cur.matches.filter(match => match.key === 'title')
            }
          }
          return acc
        }, {})
        commit('updateSearchResults', searchResults)
      }

      // Filter threads by tags
      if (queryTags && queryTags.length > 0) {
        filteredThreads = getters.threadsArr.filter(thread => {
          const tags = state.postsTagsMap[thread.id]
          return tags && tags.find(tag => queryTags.includes(tag.name))
        })
      }

      // Sort the post threads by sticky status.
      filteredThreads = filteredThreads.sort((a, b) => b.sticky - a.sticky)

      commit('updateFilteredThreads', filteredThreads)
    },

    async fetchLikesForPage ({ commit }, postsIds) {
      try {
        const totalLikes = (await axios.get(process.env.VUE_APP_API_URI + '/user-actions/group-total-likes', {
          params: {
            componentType: COMPONENT_POST,
            componentIds: postsIds
          }
        })).data
        commit('updateLikesForPage', totalLikes)
      } catch (error) {
        console.error(`Failed to fetch user group likes for the post detail page: ${error}`)
      }
    },

    async fetchPoints ({ commit }) {
      try {
        const points = (await axios.get(`${process.env.VUE_APP_API_URI}/forum/points`)).data
        commit('updatePoints', points)
      } catch (error) {
        console.error(`Failed to update points: ${error}`)
      }
    },

    async updateBestAnswer ({ commit }, { postId, replyId, postResolved }) {
      try {
        const postPromise = axios.patch(`${process.env.VUE_APP_API_URI}/forum/post/${postId}`, {
          postResolved: !postResolved
        })
        const replyPromise = axios.patch(`${process.env.VUE_APP_API_URI}/forum/reply/${replyId}`, {
          postId,
          isBestAnswer: !postResolved
        })
        await Promise.all([postPromise, replyPromise])
        commit('updateBestAnswer', { postId, replyId, resolved: !postResolved })
      } catch (error) {
        console.error(`Failed to update Best Answer: ${error}`)
      }
    },

    /**
     * Update the posts ordering given new sort rule
     */
    sortThreads ({ commit, dispatch }, sortRuleIndex) {
      commit('updateSortRuleIndex', sortRuleIndex)
      dispatch('filterThreads')
    },

    /**
     * Fetches tags data for the forum
     */
    async fetchForumTags ({ commit }) {
      try {
        const forumTags = (await axios.get(process.env.VUE_APP_API_URI + '/forum/tags')).data
        commit('updateForumTags', forumTags)
      } catch (error) {
        console.error(`Failed to fetch all forum tags: ${error}`)
      }
    }
  }
}
